import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PROPERTY_TYPES = ['house', 'unit', 'apartment', 'townhouse', 'villa', 'land', 'commercial'];

const BatchPropertyCard = ({ index, property, onUpdate, onRemove, onGenerateAI }) => {
  const [expanded, setExpanded] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [photoPreview, setPhotoPreview] = useState([]);

  const update = (field, value) => onUpdate(index, { ...property, [field]: value });

  const handlePhotoUpload = (e) => {
    const files = Array.from(e?.target?.files);
    const previews = files?.map(f => ({ name: f?.name, url: URL.createObjectURL(f) }));
    setPhotoPreview(prev => [...prev, ...previews]);
    update('photos', [...(property?.photos || []), ...files]);
  };

  const handleGenerateAI = async () => {
    setIsGenerating(true);
    await new Promise(r => setTimeout(r, 1500));
    const desc = `This stunning ${property?.type || 'property'} at ${property?.address || 'prime location'} features ${property?.bedrooms || 3} bedrooms and ${property?.bathrooms || 2} bathrooms. Perfectly positioned in a sought-after area, this property offers exceptional value and lifestyle opportunities.`;
    update('description', desc);
    setIsGenerating(false);
    onGenerateAI?.(index);
  };

  const isValid = property?.address && property?.price && property?.bedrooms && property?.bathrooms && property?.type;

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${isValid ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
      {/* Card Header */}
      <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
            isValid ? 'bg-green-400/20 text-green-400' : 'bg-white/10 text-slate-400'
          }`}>
            {isValid ? <Icon name="Check" size={14} color="#22C55E" /> : index + 1}
          </div>
          <div>
            <p className="text-white font-medium text-sm">
              {property?.address || `Property ${index + 1}`}
            </p>
            <p className="text-slate-400 text-xs">
              {property?.type && property?.bedrooms ? `${property?.type} • ${property?.bedrooms}bd ${property?.bathrooms}ba` : 'Fill in property details'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e?.stopPropagation(); onRemove(index); }}
            className="p-1.5 rounded-lg hover:bg-red-400/10 transition-colors"
          >
            <Icon name="Trash2" size={14} color="#F87171" />
          </button>
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={16} color="#94A3B8" />
        </div>
      </div>
      {/* Card Body */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Address */}
            <div className="md:col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Address <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={property?.address || ''}
                onChange={e => update('address', e?.target?.value)}
                placeholder="123 Main Street, Sydney NSW 2000"
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-slate-500 outline-none focus:ring-1 focus:ring-cyan-400/50"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            {/* Price */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Price <span className="text-red-400">*</span></label>
              <input
                type="number"
                value={property?.price || ''}
                onChange={e => update('price', e?.target?.value)}
                placeholder="750000"
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-slate-500 outline-none focus:ring-1 focus:ring-cyan-400/50"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            {/* Property Type */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Property Type <span className="text-red-400">*</span></label>
              <select
                value={property?.type || ''}
                onChange={e => update('type', e?.target?.value)}
                className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-1 focus:ring-cyan-400/50"
                style={{ background: 'rgba(20,30,50,0.9)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <option value="">Select type</option>
                {PROPERTY_TYPES?.map(t => <option key={t} value={t}>{t?.charAt(0)?.toUpperCase() + t?.slice(1)}</option>)}
              </select>
            </div>
            {/* Bedrooms */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Bedrooms <span className="text-red-400">*</span></label>
              <input
                type="number"
                min="0" max="20"
                value={property?.bedrooms || ''}
                onChange={e => update('bedrooms', e?.target?.value)}
                placeholder="3"
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-slate-500 outline-none focus:ring-1 focus:ring-cyan-400/50"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            {/* Bathrooms */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Bathrooms <span className="text-red-400">*</span></label>
              <input
                type="number"
                min="0" max="20"
                value={property?.bathrooms || ''}
                onChange={e => update('bathrooms', e?.target?.value)}
                placeholder="2"
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-slate-500 outline-none focus:ring-1 focus:ring-cyan-400/50"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
          </div>

          {/* Description + AI */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-slate-400">Description</label>
              <button
                onClick={handleGenerateAI}
                disabled={isGenerating}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
                style={{ background: 'rgba(123,47,255,0.15)', border: '1px solid rgba(123,47,255,0.3)', color: '#A78BFA' }}
              >
                {isGenerating ? (
                  <><div className="w-3 h-3 border border-purple-400 border-t-transparent rounded-full animate-spin" /> Generating...</>
                ) : (
                  <><Icon name="Sparkles" size={12} color="#A78BFA" /> AI Generate</>
                )}
              </button>
            </div>
            <textarea
              value={property?.description || ''}
              onChange={e => update('description', e?.target?.value)}
              placeholder="Describe this property..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-slate-500 outline-none focus:ring-1 focus:ring-cyan-400/50 resize-none"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>

          {/* Photo Upload */}
          <div>
            <label className="block text-xs text-slate-400 mb-2">Photos</label>
            <div className="flex items-center gap-3 flex-wrap">
              {photoPreview?.map((p, i) => (
                <div key={i} className="w-16 h-16 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                  <img src={p?.url} alt={`Property photo ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
              <label className="w-16 h-16 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-white/10 transition-colors" style={{ border: '1px dashed rgba(255,255,255,0.2)' }}>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                <Icon name="Plus" size={18} color="#94A3B8" />
                <span className="text-xs text-slate-500 mt-1">Add</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchPropertyCard;
